'use client'

import { latestBirthdateAsDate } from '@/utils/birthdate'
import { MultiSelect, SingleSelect } from '@/components/common'
import { SelectOption } from '@/types'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  CustomFlowbiteTheme,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Datepicker,
} from 'flowbite-react'
import { format, subYears } from 'date-fns'
import { useEffect, useState } from 'react'
import { AppLogo } from '@/app/web/ui'
import {
  additionalInfoBaseSchema,
  AdditionalInfoType,
} from '../Schema/AdditionalInfoSchema'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { loadAllCountries } from '@/utils/loadGeographic'
import { BACKGROUND_PROFILES, GENDER_OPTIONS } from '@/constants/registration'
import { loadAllJobs, loadAllTech } from '@/utils/loadSingleColumn'

const listGender = GENDER_OPTIONS
const listCareerLevel = BACKGROUND_PROFILES

const customDatePickerTheme: CustomFlowbiteTheme['datepicker'] = {
  root: {
    input: {
      field: {
        input: {
          base: 'block w-full border border-gray-300 white p-2.5 text-sm pl-10 rounded-lg',
          colors: {
            gray: 'white',
          },
        },
      },
    },
  },
}

const customModalTheme: CustomFlowbiteTheme['modal'] = {
  content: {
    base: 'relative h-full w-full md:h-auto p-0 sm:p-4',
    inner:
      'relative h-full sm:h-auto rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col w-full',
  },
  header: {
    base: 'flex justify-center rounded-t p-6 border-b-none w-full',
    close: {
      base: 'hidden',
      icon: 'hidden',
    },
  },
  body: {
    base: 'flex-1 overflow-auto p-6',
  },
}

interface AdditionalInfoModalProps {
  submit: (data: AdditionalInfoType) => Promise<void>
  closeModal: () => void
}

const AdditionalInfoModal = ({
  submit,
  closeModal,
}: AdditionalInfoModalProps) => {
  const [openModal, setOpenModal] = useState(true)
  const currentDate = new Date()
  // Shared with the customized registration form and the backend, so every
  // date-of-birth input in the app rejects the same dates.
  const maxDate = latestBirthdateAsDate()
  const minDate = subYears(currentDate, 100)

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<AdditionalInfoType>({
    resolver: zodResolver(additionalInfoBaseSchema),
    mode: 'onChange',
    defaultValues: {
      gender: undefined,
      birthdate: undefined,
      nationality: '',
      careerLevel: '',
      expertiseAreas: [],
      techStack: [],
    },
  })

  const [listCountry, setListCountry] = useState<Array<SelectOption>>([])
  const [listJob, setListJob] = useState<Array<SelectOption>>([])
  const [listTech, setListTech] = useState<Array<SelectOption>>([])

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const countries = await loadAllCountries()
        const jobs = await loadAllJobs()
        const techStacks = await loadAllTech()

        // Labelled with the country name, valued with the ISO alpha-2 code:
        // StackUp's Member validates nationality against its country-code
        // list, so submitting the label produces a value that cannot be
        // synced. Matches the `nationality` registration field type, which
        // labels its options the same way.
        setListCountry(
          countries
            .map((country: SelectOption & { iso2?: string }) => ({
              value: country.iso2 || '',
              label: country.label || '',
            }))
            .filter((option: SelectOption) => option.value && option.label)
        )
        setListJob(
          jobs.map(({ label }: SelectOption) => ({ value: label, label }))
        )
        setListTech(
          techStacks.map(({ label }: SelectOption) => ({ value: label, label }))
        )
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    loadOptions()
  }, [])

  const onSubmit: SubmitHandler<AdditionalInfoType> = async (data) => {
    await submit(data)
    setOpenModal(false)
  }

  return (
    <Modal
      theme={customModalTheme}
      dismissible
      show={openModal}
      initialFocus={-1}
    >
      <ModalHeader>
        <div className="flex items-center gap-2 ml-4 md:ml-0">
          <AppLogo />
        </div>
      </ModalHeader>
      <ModalBody>
        <form
          className="flex flex-col gap-y-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="w-full text-center">
            <h1 className="text-2xl font-bold mb-2">
              Additional Information Required
            </h1>
            <p className="text-gray-500">
              This will allow Hackathon to improve your experience
            </p>
          </div>
          <div className="flex flex-col">
            <Label
              value="Gender"
              htmlFor="gender"
              className="text-sm text-gray-900 font-medium mb-3"
            />
            <Controller
              control={control}
              name="gender"
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <SingleSelect
                  inputId={'gender'}
                  options={listGender}
                  canSearchDropdown={true}
                  onBlur={onBlur}
                  onSelectValue={(item) => onChange(item.value)}
                  dropdownLabel={'Select your gender'}
                  color={errors.gender?.message ? 'failure' : 'grayborder'}
                  defaultValue={listGender.find(
                    (option) => option.value === value
                  )}
                />
              )}
            />
            {errors.gender && (
              <span className="mt-2 text-sm text-red-600 dark:text-red-500">
                {errors.gender?.message}
              </span>
            )}
          </div>

          <div className="flex flex-col">
            <Label
              value="Date of Birth"
              htmlFor="date_of_birth"
              className="text-sm text-gray-900 font-medium mb-3"
            />
            <Controller
              control={control}
              name="birthdate"
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="relative">
                  <Datepicker
                    theme={customDatePickerTheme}
                    showClearButton={false}
                    showTodayButton={false}
                    defaultValue={
                      value ? new Date(value) : new Date(2000, 0, 1)
                    }
                    placeholder="Select your date of birth"
                    minDate={minDate}
                    maxDate={maxDate}
                    onBlur={onBlur}
                    color={errors.birthdate?.message ? 'failure' : 'grayborder'}
                    onChange={(date) => date && onChange(date.toString())}
                  />
                  {!value && (
                    <span className="pointer-events-none absolute inset-y-px left-10 right-px flex items-center rounded-r-lg bg-white pl-px pr-2 text-sm text-gray-400">
                      Select your date of birth
                    </span>
                  )}
                </div>
              )}
            />
            {errors.birthdate && (
              <span className="mt-2 text-sm text-red-600 dark:text-red-500">
                {errors.birthdate?.message}
              </span>
            )}
          </div>

          <div className="flex flex-col">
            <Label
              value="Nationality"
              htmlFor="nationality"
              className="text-sm text-gray-900 font-medium mb-3"
            />
            {listCountry.length > 0 && (
              <Controller
                control={control}
                name="nationality"
                render={({ field: { onChange, onBlur, value } }) => (
                  <SingleSelect
                    inputId={'nationality'}
                    options={listCountry}
                    canSearchDropdown={true}
                    onBlur={onBlur}
                    onSelectValue={(item) => onChange(item.value)}
                    defaultValue={listCountry.find(
                      (item) => item.value === value
                    )}
                    dropdownLabel={'Select your nationality'}
                    color={
                      errors.nationality?.message ? 'failure' : 'grayborder'
                    }
                  />
                )}
              />
            )}
            {errors.nationality && (
              <span className="mt-2 text-sm text-red-600 dark:text-red-500">
                {errors.nationality?.message}
              </span>
            )}
          </div>

          <div className="flex flex-col">
            <Label
              value="Career Level"
              htmlFor="careerLevel"
              className="text-sm text-gray-900 font-medium mb-3"
            />
            <Controller
              control={control}
              name="careerLevel"
              render={({ field: { onChange, onBlur, value } }) => (
                <SingleSelect
                  inputId={'careerLevel'}
                  options={listCareerLevel}
                  canSearchDropdown={true}
                  onBlur={onBlur}
                  onSelectValue={(item) => onChange(item.value)}
                  defaultValue={listCareerLevel.find(
                    (item) => item.value === value
                  )}
                  dropdownLabel={'Select your career level'}
                  color={errors.careerLevel?.message ? 'failure' : 'grayborder'}
                />
              )}
            />
            {errors.careerLevel && (
              <span className="mt-2 text-sm text-red-600 dark:text-red-500">
                {errors.careerLevel?.message}
              </span>
            )}
          </div>

          <div className="flex flex-col">
            <Label
              value="Roles/Expertise Areas"
              htmlFor="expertise"
              className="text-sm text-gray-900 font-medium mb-3"
            />
            <Controller
              control={control}
              name="expertiseAreas"
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  options={listJob}
                  onSelectValue={(val) =>
                    onChange(val.map((option) => option.label))
                  }
                  closeMenuOnSelect={false}
                  defaultValue={listJob.filter((option) =>
                    value.includes(option.label)
                  )}
                  placeholder="Select all that applies"
                  color={
                    errors.expertiseAreas?.message ? 'failure' : 'grayborder'
                  }
                ></MultiSelect>
              )}
            />
            {errors.expertiseAreas && (
              <span className="mt-2 text-sm text-red-600 dark:text-red-500">
                {errors.expertiseAreas?.message}
              </span>
            )}
          </div>

          <div className="flex flex-col">
            <Label
              value="Tech Stack"
              htmlFor="technologies"
              className="text-sm text-gray-900 font-medium mb-3"
            />
            <Controller
              control={control}
              name="techStack"
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  options={listTech}
                  onSelectValue={(val) =>
                    onChange(val.map((option) => option.label))
                  }
                  closeMenuOnSelect={false}
                  defaultValue={listTech.filter((option) =>
                    value.includes(option.label)
                  )}
                  placeholder="Select all that applies"
                  color={errors.techStack?.message ? 'failure' : 'grayborder'}
                ></MultiSelect>
              )}
            />
            {errors.techStack && (
              <span className="mt-2 text-sm text-red-600 dark:text-red-500">
                {errors.techStack?.message}
              </span>
            )}
          </div>

          <div className="flex gap-4 w-full space-between">
            <Button
              className="border border-gray-200 w-full"
              color={'white'}
              onClick={() => {
                setOpenModal(false)
                closeModal()
              }}
            >
              Not now
            </Button>
            <Button
              type="submit"
              className="w-full disable:opacity-50"
              color={'dark'}
              disabled={!isValid}
            >
              Submit
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  )
}

export default AdditionalInfoModal
